import { useMemo, useState } from 'react'
import { Navigate, useParams } from 'react-router-dom'
import { UserCheck, Stethoscope, HelpCircle } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Label } from '@/components/ui/label'
import { SearchableSelect, type FieldOption } from '../components/SearchableSelect'
import {
  useConfiguracionFidelizacion,
  useMetadataFidelizacion,
  useTodasFacturasFidelizacion,
  useTodosParticipantesFidelizacion,
} from '../hooks/useFidelizacion'
import { parseFidelizacionError } from '../hooks/useFidelizacionError'
import { normalizeColor } from '../lib/reporteColor'
import { CAMPO_REPRESENTANTE_CANDIDATES, pickCampo } from '../lib/campos'
import { representanteMedifarmaPeru } from '../lib/representantesMedifarmaPeru'
import { mapaPreciosReporte, valorProductoReferencia } from '../lib/preciosReferencia'
import { ReporteHeader } from '../components/ReporteHeader'
import { ReporteSubNav } from '../components/ReporteSubNav'
import { ReportePeriodoBar } from '../components/ReportePeriodoBar'
import { usePeriodoReporte } from '../hooks/usePeriodoReporte'
import { formatMoneda } from '../lib/paisMapa'
import { BarChartRepresentante, type RepresentanteDatum } from '../components/charts/BarChartRepresentante'

// La tab Representantes cruza el campo `representante` del upstream con el
// catálogo del Excel RRVV/RRMM; solo tiene sentido en Medifarma Perú (id 7).
const CONFIG_ID = 7

const ESTADOS_VENTAS = new Set(['confirmada', 'aprobada', 'validada', 'corregida'])

// created_at llega como ISO UTC. Convertir a fecha Perú (UTC-5) para no
// incluir registros de la noche del día anterior que quedan en UTC al día siguiente.
const TZ_PERU = 'America/Lima'
function toLocalDate(iso: string): string {
  const d = new Date(iso)
  if (isNaN(d.getTime())) return iso.slice(0, 10)
  return new Intl.DateTimeFormat('en-CA', { timeZone: TZ_PERU, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d)
}

export function RepresentantesFidelizacionPage() {
  const { id } = useParams<{ id: string }>()
  const configId = id ? parseInt(id, 10) : 0

  const config = useConfiguracionFidelizacion(configId)
  const metadata = useMetadataFidelizacion(configId)

  const {
    mes,
    setMes,
    sinFecha,
    setSinFecha,
    modo,
    setModo,
    rangoPersonalizado,
    setRangoPersonalizado,
    rango,
    rangoEfectivo,
  } = usePeriodoReporte()

  const camposExternos = metadata.data?.campos_externos
  const campoRepresentante = useMemo(
    () => pickCampo(camposExternos, CAMPO_REPRESENTANTE_CANDIDATES),
    [camposExternos],
  )

  // Filtros client-side: la tab ya carga el universo completo.
  // - repSeleccionado: representante puntual (global, todas las secciones).
  // - jefeVentas: Jefe de Zona del RRVV → acota solo la gráfica de ventas.
  // - repMedico: Supervisor del RRMM → acota solo la gráfica de médicos.
  const [repSeleccionado, setRepSeleccionado] = useState<string | undefined>(undefined)
  const [jefeVentas, setJefeVentas] = useState<string | undefined>(undefined)
  const [repMedico, setRepMedico] = useState<string | undefined>(undefined)

  // Todos los participantes y facturas; el filtrado fino es client-side con
  // toLocalDate. Las facturas se acotan por rango server-side para no chocar con
  // el tope de paginación (~20k): sin esto, en tenants grandes (ej. Medifarma)
  // las ventas $ por representante salían subcontadas.
  const todosParticipantes = useTodosParticipantesFidelizacion(configId)
  const todasFacturas = useTodasFacturasFidelizacion(configId, {
    desde: rangoEfectivo.desde,
    hasta: rangoEfectivo.hasta,
  })

  // Estado combinado para los charts: dependen de participantes (registros) y de
  // facturas (ventas $); mostrar "listo" antes de tener ambos daba lectura de 0 ventas.
  const cargandoDatos = todosParticipantes.isLoading || todasFacturas.isLoading
  const errorDatos = todosParticipantes.error || todasFacturas.error

  // Lista de precios de referencia para valorar ventas en S./ (Medifarma Perú).
  const mapaPrecios = useMemo(
    () => mapaPreciosReporte({ id: configId, slug: config.data?.slug, nombre: config.data?.nombre }),
    [configId, config.data?.slug, config.data?.nombre],
  )

  // Participantes dentro del periodo seleccionado usando timezone Perú.
  const participantesEnRango = useMemo(() => {
    const { desde, hasta } = rangoEfectivo
    return (todosParticipantes.data ?? []).filter((p) => {
      if (!desde && !hasta) return true
      const f = p.created_at ? toLocalDate(String(p.created_at)) : ''
      if (desde && f < desde) return false
      if (hasta && f > hasta) return false
      return true
    })
  }, [todosParticipantes.data, rangoEfectivo])

  // Map participante_id → nombre representante (todos los participantes, no solo en rango).
  const participanteRepMap = useMemo(() => {
    const map = new Map<number, string>()
    const k = campoRepresentante?.key
    for (const p of todosParticipantes.data ?? []) {
      const extra = (p.datos_extra as Record<string, unknown> | undefined) ?? {}
      const repNombre = k ? String(extra[`${k}_nombre`] ?? extra[k] ?? '') : ''
      if (p.id && repNombre && repNombre !== '(sin asignar)') {
        map.set(Number(p.id), repNombre)
      }
    }
    return map
  }, [todosParticipantes.data, campoRepresentante?.key])

  // Facturas válidas del periodo filtradas por fecha_carga en hora Perú.
  const facturasEnRango = useMemo(() => {
    const { desde, hasta } = rangoEfectivo
    return (todasFacturas.data ?? []).filter((f) => {
      if (!ESTADOS_VENTAS.has(String(f.estado ?? '').toLowerCase())) return false
      if (!desde && !hasta) return true
      const cargaDate = f.fecha_carga ? toLocalDate(String(f.fecha_carga)) : ''
      if (desde && cargaDate < desde) return false
      if (hasta && cargaDate > hasta) return false
      return true
    })
  }, [todasFacturas.data, rangoEfectivo])

  // Opción {value,label} única y ordenada a partir de un set de nombres.
  const aOpciones = (nombres: Iterable<string>): FieldOption[] =>
    Array.from(new Set(nombres))
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' }))
      .map((n) => ({ value: n, label: n }))

  // Opciones de los tres filtros, derivadas del universo cargado (independientes
  // de la selección): representantes, Jefes de Zona (RRVV) y Supervisores (RRMM).
  const { opcionesRep, opcionesJefe, opcionesMedico } = useMemo(() => {
    const reps: string[] = []
    const jefes: string[] = []
    const supervisores: string[] = []
    const k = campoRepresentante?.key
    for (const p of participantesEnRango) {
      const extra = (p.datos_extra as Record<string, unknown> | undefined) ?? {}
      const repNombre = k ? String(extra[`${k}_nombre`] ?? extra[k] ?? '') : ''
      if (!repNombre || repNombre === '(sin asignar)') continue
      reps.push(repNombre)
      const info = representanteMedifarmaPeru(configId, repNombre)?.[0]
      if (info?.tipo === 'RRVV' && info.jefeZona) jefes.push(info.jefeZona)
      else if (info?.tipo === 'RRMM' && info.supervisor) supervisores.push(info.supervisor)
    }
    return {
      opcionesRep: aOpciones(reps),
      opcionesJefe: aOpciones(jefes),
      opcionesMedico: aOpciones(supervisores),
    }
  }, [participantesEnRango, configId, campoRepresentante?.key])

  // Agrupa por Jefe de Zona (RRVV) y Supervisor (RRMM).
  // Pasada 1: registros desde participantes filtrados por fecha.
  // Pasada 2: monto (ventas $) desde facturas filtradas por fecha_carga.
  const { ventas, medicos, sinClasificar } = useMemo(() => {
    const ventasMap = new Map<string, RepresentanteDatum>()
    const medicosMap = new Map<string, RepresentanteDatum>()
    const sinClasificarMap = new Map<string, RepresentanteDatum>()
    const k = campoRepresentante?.key

    // Pasada 1: contar registros desde participantes en rango
    for (const p of participantesEnRango) {
      const extra = (p.datos_extra as Record<string, unknown> | undefined) ?? {}
      const repNombre = k ? String(extra[`${k}_nombre`] ?? extra[k] ?? '') : ''
      if (!repNombre || repNombre === '(sin asignar)') continue
      if (repSeleccionado && repNombre !== repSeleccionado) continue
      const info = representanteMedifarmaPeru(configId, repNombre)?.[0]
      if (info?.tipo === 'RRVV') {
        const key = info.jefeZona
        if (jefeVentas && key !== jefeVentas) continue
        const prev = ventasMap.get(key)
        if (prev) prev.registros += 1
        else ventasMap.set(key, { label: key, registros: 1, ventas: 0 })
      } else if (info?.tipo === 'RRMM') {
        const key = info.supervisor
        if (repMedico && key !== repMedico) continue
        const prev = medicosMap.get(key)
        if (prev) prev.registros += 1
        else medicosMap.set(key, { label: key, registros: 1, ventas: 0 })
      } else {
        const prev = sinClasificarMap.get(repNombre)
        if (prev) prev.registros += 1
        else sinClasificarMap.set(repNombre, { label: repNombre, registros: 1, ventas: 0 })
      }
    }

    // Pasada 2: sumar ventas S./ desde facturas usando precio referencia × cantidad.
    // Los productos del listado usan el campo `cantidad` (no `cantidad_vendida`).
    if (mapaPrecios) {
      for (const f of facturasEnRango) {
        const repNombre = participanteRepMap.get(Number(f.participante_id)) ?? ''
        if (!repNombre || repNombre === '(sin asignar)') continue
        if (repSeleccionado && repNombre !== repSeleccionado) continue
        const productos = Array.isArray(f.productos) ? f.productos as Array<Record<string, unknown>> : []
        const montoFactura = productos.reduce(
          (sum, p) => sum + valorProductoReferencia(mapaPrecios, {
            nombre: String(p.nombre ?? ''),
            cantidad_vendida: p.cantidad,
            monto: p.monto,
          }),
          0,
        )
        const info = representanteMedifarmaPeru(configId, repNombre)?.[0]
        if (info?.tipo === 'RRVV') {
          const key = info.jefeZona
          if (jefeVentas && key !== jefeVentas) continue
          const entry = ventasMap.get(key)
          if (entry) entry.ventas += montoFactura
        } else if (info?.tipo === 'RRMM') {
          const key = info.supervisor
          if (repMedico && key !== repMedico) continue
          const entry = medicosMap.get(key)
          if (entry) entry.ventas += montoFactura
        } else {
          const entry = sinClasificarMap.get(repNombre)
          if (entry) entry.ventas += montoFactura
        }
      }
    }

    return {
      ventas: Array.from(ventasMap.values()),
      medicos: Array.from(medicosMap.values()),
      sinClasificar: Array.from(sinClasificarMap.values()),
    }
  }, [participantesEnRango, facturasEnRango, participanteRepMap, mapaPrecios, configId, campoRepresentante?.key, repSeleccionado, jefeVentas, repMedico])

  const color = normalizeColor(config.data)
  const errorGlobal = config.error || metadata.error
  const refreshing = metadata.isFetching || todosParticipantes.isFetching || todasFacturas.isFetching

  const handleRefresh = () => {
    config.refetch()
    metadata.refetch()
    todosParticipantes.refetch()
    todasFacturas.refetch()
  }

  // La tab solo aplica a Medifarma Perú; cualquier otro reporte vuelve al resumen.
  if (configId !== CONFIG_ID) {
    return <Navigate to={`/reportes-fidelizacion/${configId}`} replace />
  }

  const formatVentas = (v: number) => formatMoneda(v, configId, { maximumFractionDigits: 0 })

  return (
    <div className="space-y-6">
      <ReporteHeader
        config={config.data}
        programaNombre={metadata.data?.programa.nombre}
        icon={UserCheck}
        seccion="Representantes"
        onRefresh={handleRefresh}
        refreshing={refreshing}
      />

      <ReporteSubNav configId={configId} colorNombre={color.nombre} colorTono={color.tono} />

      <ReportePeriodoBar
        rango={rango}
        mes={mes}
        onMesChange={setMes}
        minDate={metadata.data?.rango_datos?.primera_factura ?? null}
        maxDate={metadata.data?.rango_datos?.ultima_factura ?? null}
        configId={configId}
        colorNombre={color.nombre}
        exportTipos={[]}
        sinFecha={sinFecha}
        onSinFechaChange={setSinFecha}
        modo={modo}
        onModoChange={setModo}
        rangoPersonalizado={rangoPersonalizado}
        onRangoPersonalizadoChange={setRangoPersonalizado}
      />

      {errorGlobal && (
        <Alert variant="destructive">
          <AlertDescription>{parseFidelizacionError(errorGlobal).message}</AlertDescription>
        </Alert>
      )}

      {/* Filtros client-side sobre el universo ya cargado. */}
      <div className="rounded-lg border border-gray-300 dark:border-gray-700 p-4">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div className="space-y-1.5">
            <Label htmlFor="f-representante" className="text-xs">
              {campoRepresentante?.label ?? 'Representante'}
            </Label>
            <SearchableSelect
              id="f-representante"
              placeholder="Todos"
              options={opcionesRep}
              value={repSeleccionado}
              onChange={setRepSeleccionado}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="f-jefe-ventas" className="text-xs">
              Jefe de Ventas
            </Label>
            <SearchableSelect
              id="f-jefe-ventas"
              placeholder="Todos"
              options={opcionesJefe}
              value={jefeVentas}
              onChange={setJefeVentas}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="f-rep-medico" className="text-xs">
              Supervisor
            </Label>
            <SearchableSelect
              id="f-rep-medico"
              placeholder="Todos"
              options={opcionesMedico}
              value={repMedico}
              onChange={setRepMedico}
            />
          </div>
        </div>
      </div>

      <div className="grid gap-4 xl:grid-cols-2 items-stretch">
        <BarChartRepresentante
          title="Registros por Jefe de Zona (RRVV)"
          data={ventas}
          colorNombre={color.nombre}
          isLoading={cargandoDatos}
          error={errorDatos}
          icon={UserCheck}
          formatVentas={formatVentas}
          configId={configId}
        />
        <BarChartRepresentante
          title="Registros por Supervisor (RRMM)"
          data={medicos}
          colorNombre={color.nombre}
          isLoading={cargandoDatos}
          error={errorDatos}
          icon={Stethoscope}
          formatVentas={formatVentas}
          configId={configId}
        />
        {sinClasificar.length > 0 && (
          <BarChartRepresentante
            title="Registros sin clasificar"
            data={sinClasificar}
            colorNombre={color.nombre}
            isLoading={cargandoDatos}
            error={errorDatos}
            icon={HelpCircle}
            formatVentas={formatVentas}
            configId={configId}
          />
        )}
      </div>
    </div>
  )
}
